import { AGENT_IDS } from '../constants/agents';

const PENDING_DEEP_LINK_KEY = 'coop-gpt.pending-home-deep-link';
const PENDING_DEEP_LINK_TTL_MS = 10 * 60 * 1000;
const MAX_PROMPT_LENGTH = 2000;

export type HomeDeepLink = {
  hasPrompt: boolean;
  hasAgent: boolean;
  prompt: string | null;
  promptError: string | null;
  agent: string | null;
  agentError: string | null;
};

type StoredHomeDeepLink = HomeDeepLink & { expiresAt: number };

const removePendingHomeDeepLink = (): void => {
  try {
    sessionStorage.removeItem(PENDING_DEEP_LINK_KEY);
  } catch {
    console.warn('Unable to clear the pending chat link from session storage.');
  }
};

const decodeQueryComponent = (value: string): string => decodeURIComponent(value.replace(/\+/g, ' '));

const getQueryEntries = (search: string): Array<[string, string]> => {
  const query = search.startsWith('?') ? search.slice(1) : search;
  const entries: Array<[string, string]> = [];

  for (const part of query.split('&')) {
    if (!part) continue;
    const separator = part.indexOf('=');
    const rawKey = separator === -1 ? part : part.slice(0, separator);
    const rawValue = separator === -1 ? '' : part.slice(separator + 1);

    try {
      entries.push([decodeQueryComponent(rawKey), rawValue]);
    } catch {
      // An invalid key cannot be a valid deep-link parameter.
    }
  }

  return entries;
};

export const parseHomeDeepLink = (search: string): HomeDeepLink => {
  const entries = getQueryEntries(search);
  const promptEntries = entries.filter(([key]) => key === 'prompt');
  const agentEntries = entries.filter(([key]) => key === 'agent');

  let prompt: string | null = null;
  let promptError: string | null = null;
  if (promptEntries.length > 1) {
    promptError = 'This link contains more than one prompt.';
  } else if (promptEntries.length === 1) {
    try {
      prompt = decodeQueryComponent(promptEntries[0][1]);
      if (!prompt.trim()) {
        promptError = 'The prompt cannot be blank.';
        prompt = null;
      } else if (Array.from(prompt).length > MAX_PROMPT_LENGTH) {
        promptError = 'The prompt must be 2,000 characters or fewer.';
        prompt = null;
      }
    } catch {
      promptError = 'The prompt in this link is malformed.';
    }
  }

  let agent: string | null = null;
  let agentError: string | null = null;
  if (agentEntries.length > 1) {
    agentError = 'This link contains more than one agent. The default assistant will be used.';
  } else if (agentEntries.length === 1) {
    try {
      const decodedAgent = decodeQueryComponent(agentEntries[0][1]).trim().toLowerCase();
      if (decodedAgent) {
        agent = decodedAgent;
        if (agent !== AGENT_IDS.DIGITAL_MAINTENANCE) {
          agentError = 'This link can only select Digital Maintenance. The default assistant will be used.';
        }
      }
    } catch {
      agentError = 'The agent in this link is malformed. The default assistant will be used.';
    }
  }

  return {
    hasPrompt: promptEntries.length > 0,
    hasAgent: agentEntries.length > 0,
    prompt,
    promptError,
    agent,
    agentError,
  };
};

const clearExpiredPendingDeepLink = (): void => {
  try {
    const storedValue = sessionStorage.getItem(PENDING_DEEP_LINK_KEY);
    if (!storedValue) return;
    const stored = JSON.parse(storedValue) as Partial<StoredHomeDeepLink>;
    if (typeof stored.expiresAt !== 'number' || stored.expiresAt <= Date.now()) {
      removePendingHomeDeepLink();
    }
  } catch {
    removePendingHomeDeepLink();
  }
};

export const captureHomeDeepLink = (): void => {
  if (typeof window === 'undefined' || window.location.pathname !== '/') return;

  clearExpiredPendingDeepLink();
  const parsed = parseHomeDeepLink(window.location.search);
  if (!parsed.hasPrompt && !parsed.hasAgent) return;

  const url = new URL(window.location.href);
  url.searchParams.delete('prompt');
  url.searchParams.delete('agent');
  window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);

  try {
    const stored: StoredHomeDeepLink = {
      ...parsed,
      expiresAt: Date.now() + PENDING_DEEP_LINK_TTL_MS,
    };
    sessionStorage.setItem(PENDING_DEEP_LINK_KEY, JSON.stringify(stored));
  } catch {
    // URL cleanup remains useful even when sessionStorage is unavailable.
  }
};

export const consumePendingHomeDeepLink = (): HomeDeepLink | null => {
  try {
    const storedValue = sessionStorage.getItem(PENDING_DEEP_LINK_KEY);
    removePendingHomeDeepLink();
    if (!storedValue) return null;

    const stored = JSON.parse(storedValue) as Partial<StoredHomeDeepLink>;
    if (typeof stored.expiresAt !== 'number' || stored.expiresAt <= Date.now()) return null;
    if (typeof stored.hasPrompt !== 'boolean') return null;

    return {
      hasPrompt: stored.hasPrompt,
      hasAgent: stored.hasAgent === true,
      prompt: typeof stored.prompt === 'string' ? stored.prompt : null,
      promptError: typeof stored.promptError === 'string' ? stored.promptError : null,
      agent: typeof stored.agent === 'string' ? stored.agent : null,
      agentError: typeof stored.agentError === 'string' ? stored.agentError : null,
    };
  } catch {
    removePendingHomeDeepLink();
    return null;
  }
};
